import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import hero from "../../assets/image/hero1.jpg";
import hero1 from "../../assets/image/hero2.jpg";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { quickQuoteEndpoints } from "../../api/quickQuote";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Hero = () => {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    serviceType: "",
    description: "",
    enable: true,
  });
  const [open, setOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const mappedData = {
        name: formData.name,
        mobileNumber: formData.phoneNumber,
        email: formData.email,
        serviceType: formData.serviceType,
        description: formData.description,
        enable: formData.enable,
      };

      const response = await quickQuoteEndpoints.create(mappedData);

      if (response.status === 201) {
        setFormData({
          name: "",
          phoneNumber: "",
          email: "",
          serviceType: "",
          description: "",
          enable: true,
        });
        setSeverity("success");
        setSnackbarMessage("Quote request submitted successfully!");
        setOpen(true);
      }
    } catch (error) {
      setSeverity("error");
      setSnackbarMessage("Failed to submit quote request. Please try again.");
      setOpen(true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <div className="relative">
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Main Hero Section */}
      <div className="relative bg-gray-50 min-h-[calc(121vh-136px)]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-secondary/100 mix-blend-multiply z-10" />
          <img
            className="w-full h-full object-cover"
            src={hero}
            alt="Cleaning Service"
            style={{ minHeight: "calc(100vh - 136px)" }}
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8 min-h-[calc(100vh-136px)] flex items-center justify-between">
          {/* Hero Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl"
            >
              Professional
              <span> Cleaning </span>
              {/* <span className="text-secondary-light"> Cleaning </span>  */}
              Services in Australia
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-6 text-xl text-gray-100"
            >
              Expert commercial and residential cleaning solutions tailored to
              your needs. Experience the difference with Envipro Solutions.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-10 flex gap-4"
            >
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 transform"
              >
                Get Free Quote
              </Link>
              <Link
                to="/servicepage"
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-dark hover:to-secondary transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 transform"
              >
                Our Services
              </Link>
            </motion.div>
          </motion.div>

          {/* Quick Quote Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="hidden lg:block bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl p-6 w-[800px]"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Get a Quick Quote
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-md border-gray-300 focus:border-primary focus:ring-primary"
                    placeholder="Enter your name"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="phoneNumber"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Phone Number
                  </label>
                  <input
                    type="phoneNumber"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-md border-gray-300 focus:border-primary focus:ring-primary"
                    placeholder="Enter your phone"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Enter Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-md border-gray-300 focus:border-primary focus:ring-primary"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="service"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Service Type
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-md border-gray-300 focus:border-primary focus:ring-primary"
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="commercial_cleaning">
                      Commercial Cleaning
                    </option>
                    <option value="residential_cleaning">
                      Residential Cleaning
                    </option>
                    <option value="industrial_cleaning">
                      Industrial Cleaning
                    </option>
                    <option value="specialized_cleaning">
                      Specialized Cleaning
                    </option>
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Your Message
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border-gray-300 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                  placeholder="Tell us about your cleaning needs..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center px-6 py-2 rounded-md text-white bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    Processing...
                    <svg
                      className="animate-spin ml-2 h-5 w-5"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </span>
                ) : (
                  <>
                    Request Quote <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 space-y-2">
              <div className="flex items-center text-gray-600">
                <Phone className="h-4 w-4 text-primary mr-2" />
                <span>Call us: Mob : 0452298814 Tel : 0882708801</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Mail className="h-4 w-4 text-primary mr-2" />
                <span>Email: info@enviprosolutions.com.au</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
